// src/components/dashboard/ActionBar.tsx
import type { BookingJob } from '@/lib/types/workflow';
import { WorkflowStatus } from '@/lib/types/workflow';
import { brand, fonts, styles } from '@/lib/tokens';
import { statusColors } from '@/lib/statusColors';

type Props = {
  booking: BookingJob;
  state: WorkflowStatus;
  queueIndex?: number;
  queueTotal?: number;
  onPrev?: () => void;
  onNext?: () => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
  onClaim?: () => void;
  isClaiming?: boolean;
  onEdit: () => void;
  onReject: () => void;
  onApproveAndSend: () => void;
  isApproving?: boolean;
  onCancelEdit?: () => void;
  onSaveAndFinalize?: () => void;
  onDiscard?: () => void;
  onSaveDraft?: () => void;
  onSend?: () => void;
  onBackToQueue?: () => void;
};

// Pre-review backend states. The prev/next arrows can land on these (they open
// on the 3-panel screen), where there is nothing to approve yet: the operator
// can only claim a RECEIVED booking, or wait for/see the state of the others.
const UNCLAIMED_STATUSES = ['RECEIVED', 'PROCESSING', 'FAILED'];

export function ActionBar({
  booking,
  state,
  queueIndex,
  queueTotal,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
  onClaim,
  isClaiming,
  onEdit,
  onReject,
  onApproveAndSend,
  isApproving,
  onCancelEdit,
  onSaveAndFinalize,
  onDiscard,
  onSaveDraft,
  onSend,
  onBackToQueue,
}: Props) {
  const isUnclaimed =
    state === WorkflowStatus.PENDING_REVIEW && UNCLAIMED_STATUSES.includes(booking.backendStatus ?? '');

  return (
    <div
      className="action-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        height: 'auto',
        background: brand.surface1,
        borderBottom: `1px solid ${brand.hairlineSoft}`,
        flexShrink: 0,
        gap: 16,
        overflow: 'hidden',
      }}
    >
      {/* Left — status pill + booking meta + queue position */}
      <div className="action-bar-left" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <StatusPill state={state} booking={booking} unclaimed={isUnclaimed} />
        <div className="action-bar-booking-meta" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              fontFamily: fonts.sans,
              fontSize: 14,
              fontWeight: 600,
              color: brand.onSurface,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {booking.clientName}
          </span>
          <span
            style={{
              ...styles.monoLabel,
              fontSize: 10,
              letterSpacing: '0.1em',
              marginTop: 1,
              whiteSpace: 'nowrap',
            }}
          >
            Booking ID: {booking.id}
          </span>
        </div>
        {queueIndex !== undefined && queueTotal !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              onClick={onPrev}
              disabled={!canGoPrev}
              title="Previous booking"
              style={{
                background: 'none',
                border: 'none',
                padding: '2px 4px',
                cursor: canGoPrev ? 'pointer' : 'default',
                color: canGoPrev ? brand.onSurface : brand.monoDarker,
                display: 'flex',
                alignItems: 'center',
                opacity: canGoPrev ? 1 : 0.3,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
            </button>
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: 10,
                color: brand.monoDim,
                whiteSpace: 'nowrap',
                letterSpacing: '0.05em',
              }}
            >
              {queueIndex + 1} / {queueTotal}
            </span>
            <button
              onClick={onNext}
              disabled={!canGoNext}
              title="Next booking"
              style={{
                background: 'none',
                border: 'none',
                padding: '2px 4px',
                cursor: canGoNext ? 'pointer' : 'default',
                color: canGoNext ? brand.onSurface : brand.monoDarker,
                display: 'flex',
                alignItems: 'center',
                opacity: canGoNext ? 1 : 0.3,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
            </button>
          </div>
        )}
      </div>

      {/* Right — state-based buttons */}
      <div className="action-bar-right" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Back to Queue is available in every state except PROCESSING (the
            operator is already looking at the queue there) — an operator
            mid-review, mid-edit, mid-stock-check, or on the Finalize screen
            must always be able to bail back out to the dashboard without
            finishing whatever screen they're on. Previously this only
            rendered for PENDING_REVIEW, so EDIT_MODE/STOCK_RESERVATION/
            FINALIZE had no way back to the queue at all short of browser
            back or a full reload. Reported 17 Sep 2026. */}
        {state !== WorkflowStatus.PROCESSING && onBackToQueue && (
          <>
            <BackToQueueBtn onClick={onBackToQueue}>Back to Queue</BackToQueueBtn>
            <div
              style={{
                width: 1,
                height: 20,
                background: 'rgba(var(--fg-rgb),0.08)',
                flexShrink: 0,
              }}
            />
          </>
        )}

        {isUnclaimed && (
          <>
            {booking.backendStatus === 'RECEIVED' && !isClaiming && (
              <PrimaryBtn onClick={onClaim}>Claim &amp; Extract</PrimaryBtn>
            )}
            {(booking.backendStatus === 'PROCESSING' || isClaiming) && (
              <span
                role="status"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: fonts.sans,
                  fontSize: 12,
                  fontWeight: 500,
                  color: brand.accent,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: brand.accent,
                    animation: 'dotPulse 1.2s ease-in-out infinite',
                  }}
                />
                AI extracting...
              </span>
            )}
            {booking.backendStatus === 'FAILED' && (
              <span
                style={{
                  fontFamily: fonts.sans,
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'rgba(var(--fg-rgb),0.5)',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Processing failed - retry from inbox
              </span>
            )}
          </>
        )}

        {state === WorkflowStatus.PENDING_REVIEW && !isUnclaimed && (
          <>
            <GhostBtn onClick={onEdit}>Edit</GhostBtn>
            <DangerBtn onClick={onReject}>Reject</DangerBtn>
            <PrimaryBtn
              onClick={isApproving ? undefined : onApproveAndSend}
              style={isApproving ? { opacity: 0.6, cursor: 'default', pointerEvents: 'none' } : undefined}
            >
              {isApproving
                ? booking.rentalDateRange
                  ? 'Checking Stock...'
                  : 'Sending...'
                : booking.rentalDateRange
                  ? 'Approve & Check Stock'
                  : 'Approve & Send'}
            </PrimaryBtn>
          </>
        )}

        {state === WorkflowStatus.EDIT_MODE && (
          <>
            <button
              onClick={onCancelEdit}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(var(--fg-rgb),0.6)',
                fontSize: 12,
                fontFamily: fonts.sans,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '4px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              Cancel
            </button>
            <PrimaryBtn
              onClick={isApproving ? undefined : onSaveAndFinalize}
              style={isApproving ? { opacity: 0.6, cursor: 'default', pointerEvents: 'none' } : undefined}
            >
              {isApproving
                ? booking.rentalDateRange
                  ? 'Checking Stock...'
                  : 'Sending...'
                : booking.rentalDateRange
                  ? 'Save, Approve & Check Stock'
                  : 'Save, Approve & Send'}
            </PrimaryBtn>
          </>
        )}

        {/* STOCK_RESERVATION: no buttons — panel has its own CTA */}

        {state === WorkflowStatus.FINALIZE && (
          <>
            <button
              onClick={onDiscard}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(var(--fg-rgb),0.6)',
                fontSize: 12,
                fontFamily: fonts.sans,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '4px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              Discard
            </button>
            <GhostBtn onClick={onSaveDraft}>Save Draft</GhostBtn>
            <PrimaryBtn
              onClick={onSend}
              style={{ boxShadow: '0 0 15px rgba(var(--fg-rgb),0.2)' }}
            >
              Finalize &amp; Send
            </PrimaryBtn>
          </>
        )}

        {state === WorkflowStatus.PROCESSING && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: fonts.sans,
              fontSize: 12,
              fontWeight: 500,
              color: 'rgba(var(--fg-rgb),0.5)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              className="status-dot"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'rgba(var(--fg-rgb),0.3)',
                animation: 'dotPulse 1.5s ease-in-out infinite',
              }}
            />
            {booking.backendStatus === 'RECEIVED' ? 'AWAITING PROCESSING' : 'AI EXTRACTING...'}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Button helpers
// ---------------------------------------------------------------------------

function BackToQueueBtn({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: brand.canvasWarm,
        border: `1px solid ${brand.accentMid}`,
        borderRadius: 8,
        color: brand.accent,
        cursor: 'pointer',
        fontFamily: fonts.sans,
        fontSize: 12,
        padding: '4px 12px',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function GhostBtn({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} style={{ ...styles.ghostBtn }}>
      {children}
    </button>
  );
}

function DangerBtn({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} style={{ ...styles.dangerBtn }}>
      {children}
    </button>
  );
}

function PrimaryBtn({
  onClick,
  children,
  style,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <button onClick={onClick} style={{ ...styles.primaryBtn, ...style }}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

const PILL_LABEL: Partial<Record<WorkflowStatus, string>> = {
  [WorkflowStatus.PENDING_REVIEW]: 'Pending Review',
  [WorkflowStatus.PROCESSING]: 'Processing',
  [WorkflowStatus.STOCK_RESERVATION]: 'Reserving Stock',
  [WorkflowStatus.EDIT_MODE]: 'Edit Mode',
  [WorkflowStatus.FINALIZE]: 'Finalizing',
  [WorkflowStatus.SENT]: 'Sent',
  [WorkflowStatus.REJECTED]: 'Rejected',
};

const UNCLAIMED_PILL_LABEL: Record<string, string> = {
  RECEIVED: 'Inbound',
  PROCESSING: 'Processing',
  FAILED: 'Failed',
};

function StatusPill({
  state,
  booking,
  unclaimed,
}: {
  state: WorkflowStatus;
  booking: BookingJob;
  unclaimed: boolean;
}) {
  const label = unclaimed
    ? UNCLAIMED_PILL_LABEL[booking.backendStatus ?? ''] ?? 'Inbound'
    : PILL_LABEL[state];
  if (!label) return null;
  const t = statusColors(unclaimed ? WorkflowStatus.PROCESSING : state);

  return (
    <div
      style={{
        display:       'flex',
        alignItems:    'center',
        gap:           6,
        padding:       '4px 12px',
        borderRadius:  9999,
        background:    t.background,
        border:        `1px solid ${t.border}`,
        color:         t.color,
        fontFamily:    fonts.sans,
        fontSize:      11,
        fontWeight:    500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace:    'nowrap',
      }}
    >
      <span
        className="status-dot"
        style={{ background: t.color }}
        aria-hidden
      />
      {label}
    </div>
  );
}
